import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// No request should be able to hang the UI indefinitely (the Azure Functions
// gateway itself gives up at ~230s, and the browser will happily wait forever
// for a stalled connection).
const REQUEST_TIMEOUT_MS = 45000;
const isTimeout = (error) => error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
  || /timeout/i.test(error?.message || '');

// File validation constants
const FILE_SIZE_LIMITS = {
  VIDEO: 1024 * 1024 * 1024, // 1GB for videos
  IMAGE: 10 * 1024 * 1024,   // 10MB for images
  DEFAULT: 10 * 1024 * 1024  // 10MB for other files
};

// Storage tiers — mirrors STORAGE_TIERS in routes-course/src/db/constant.js
// The actual limit for the current user is returned by GET /user/storage-usage.
const STORAGE_TIERS = {
  DEFAULT: 5 * 1024 * 1024 * 1024,  // 5 GB  — default
  TIER_10: 10 * 1024 * 1024 * 1024, // 10 GB — paid
  TIER_20: 20 * 1024 * 1024 * 1024, // 20 GB — paid
};
// Fallback used only when the storage-usage endpoint is unavailable
const USER_STORAGE_LIMIT = STORAGE_TIERS.DEFAULT;

// File validation functions
export const validateFileSize = (file) => {
  const fileType = file.type.toLowerCase();
  let maxSize;
  
  if (fileType.startsWith('video/')) {
    maxSize = FILE_SIZE_LIMITS.VIDEO;
  } else if (fileType.startsWith('image/')) {
    maxSize = FILE_SIZE_LIMITS.IMAGE;
  } else {
    maxSize = FILE_SIZE_LIMITS.DEFAULT;
  }
  
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    const fileSizeMB = file.size / (1024 * 1024);
    
    if (fileType.startsWith('video/')) {
      throw new Error(`Video file is too large (${fileSizeMB.toFixed(1)}MB). Maximum allowed size is ${maxSizeMB}MB (1GB).`);
    } else if (fileType.startsWith('image/')) {
      throw new Error(`Image file is too large (${fileSizeMB.toFixed(1)}MB). Maximum allowed size is ${maxSizeMB}MB.`);
    } else {
      throw new Error(`File is too large (${fileSizeMB.toFixed(1)}MB). Maximum allowed size is ${maxSizeMB}MB.`);
    }
  }
  
  return true;
};

export const validateMultipleFiles = (files) => {
  const fileArray = Array.isArray(files) ? files : Array.from(files);
  
  // Validate each file individually
  fileArray.forEach(file => validateFileSize(file));
  
  // Calculate total size of all files being uploaded
  const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
  const totalSizeMB = totalSize / (1024 * 1024);
  
  if (totalSize > USER_STORAGE_LIMIT) {
    throw new Error(`Total upload size (${totalSizeMB.toFixed(1)}MB) exceeds the 2GB limit per upload session.`);
  }
  
  return true;
};

// Function to check user's current storage usage
export const checkUserStorageUsage = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/user/storage-usage`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    // Backend returns { result: { usedStorage, storageLimit, storageExempt } }
    const usage = response.data?.result || {};
    const used = usage.usedStorage || 0;
    const limit = usage.storageLimit || USER_STORAGE_LIMIT;
    const exempt = usage.storageExempt || false;
    return {
      used,
      limit,
      exempt, // if true, skip enforcement — org is exempt
      available: exempt ? Infinity : limit - used,
    };
  } catch (error) {
    console.warn('Could not fetch storage usage:', error.message);
    // Return conservative estimate if we can't check
    return {
      used: 0,
      limit: USER_STORAGE_LIMIT,
      available: USER_STORAGE_LIMIT,
      unknown: true,
    };
  }
};

// Enhanced file upload function with validation
export const uploadMediaAttachment = async (qualificationId, moduleId, unitId, files, title, description) => {
  const token = localStorage.getItem('token');
  
  try {
    // Validate files before upload
    validateMultipleFiles(files);
    
    // Check user's storage quota
    const storageInfo = await checkUserStorageUsage();
    const uploadSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    
    if (uploadSize > storageInfo.available) {
      const uploadSizeGB = uploadSize / (1024 * 1024 * 1024);
      const availableGB = storageInfo.available / (1024 * 1024 * 1024);
      const limitGB = (storageInfo.limit / (1024 * 1024 * 1024)).toFixed(0);
      throw new Error(`Upload size (${uploadSizeGB.toFixed(2)}GB) exceeds your remaining storage (${availableGB.toFixed(2)}GB). You have used ${(storageInfo.used / (1024 * 1024 * 1024)).toFixed(2)}GB of your ${limitGB}GB limit.`);
    }
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('qualificationId', qualificationId);
    if (moduleId) formData.append('moduleId', moduleId);
    if (unitId) formData.append('unitId', unitId);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    
    // Add all files to FormData
    Array.from(files).forEach((file, index) => {
      formData.append(`files`, file);
    });
    
    const response = await axios.post(`${API_BASE_URL}/upload-media`, formData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // Remove timeout for large file uploads
      onUploadProgress: () => {
        // You can emit this to a progress callback if needed
      }
    });
    
    return response.data;
  } catch (error) {
    if (error.message.includes('file is too large') || 
        error.message.includes('exceeds') || 
        error.message.includes('Maximum allowed')) {
      // Re-throw validation errors as-is
      throw error;
    }
    
    console.error('Error uploading media:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error uploading media files");
  }
};

// Function to fetch qualification data from the API
export const getQualifications = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/qualification`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to fetch qualification data");
    }
  } catch (error) {
    console.error('Error fetching qualification data:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching qualification data");
  }
};

export const getQualificationModules = async (qualificationId) => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/unitsBehaviour`, {
      params: { id: qualificationId },
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to fetch modules data");
    }
  } catch (error) {
    console.error('Error fetching modules data:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching modules data");
  }
};

// Function to get media for a specific unit
export const getUnitMedia = async (qualificationId, moduleId, unitId) => {
  const token = localStorage.getItem('token');
  try {
    
    const response = await axios.get(`${API_BASE_URL}/getAttechmentByModules`, {
      params: { 
        qualificationId,
        moduleId,
        unitId 
      },
      headers: {
        'Authorization': `${token}`,
        'Accept': '*/*', // Accept any content type
      },
      responseType: 'blob', // Set response type to blob for media files
    });
    
    if (response.data) {
      return {
        data: response.data,
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length']
      };
    } else {
      throw new Error("Failed to fetch media data");
    }
  } catch (error) {
    console.error('Error fetching media:', error);
    
    // Provide more detailed error information
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.status, error.response.statusText);
      throw new Error(`Error ${error.response.status}: ${error.response.statusText}`);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received for media request');
      throw new Error('No response received from server');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up media request:', error.message);
      throw new Error(`Error fetching media: ${error.message}`);
    }
  }
};

// Function to update course details if needed in the future
export const updateCourseDetails = async (courseId, courseData) => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.put(`${API_BASE_URL}/qualification/${courseId}`, courseData, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to update course data");
    }
  } catch (error) {
    console.error('Error updating course data:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error updating course data");
  }
};

// New function for module grade updates
export const updateModuleGrades = async (gradeData) => {
  const token = localStorage.getItem('token');
  try {
    
    const response = await axios.patch(`${API_BASE_URL}/updatePredictedGrade`, gradeData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to update module grades");
    }
  } catch (error) {
    console.error('Error updating module grades:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error updating module grades");
  }
};

// New function for unit grade updates
export const updateUnitGrades = async (gradeData) => {
  const token = localStorage.getItem('token');
  try {
    
    const response = await axios.patch(`${API_BASE_URL}/updateUnitGrade`, gradeData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to update unit grades");
    }
  } catch (error) {
    console.error('Error updating unit grades:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error updating unit grades");
  }
};

// New function for overall qualification grade updates
export const updateQualificationGrade = async (gradeData) => {
  const token = localStorage.getItem('token');
  try {
    
    const response = await axios.patch(`${API_BASE_URL}/updateUserOverallGrade`, gradeData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Failed to update qualification grade");
    }
  } catch (error) {
    console.error('Error updating qualification grade:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error updating qualification grade");
  }
};

export const getMediaAttachments = async (qualificationId, moduleId = null, unitId = null) => {
  const token = localStorage.getItem('token');
  
  try {
    let response;
    
    if (!moduleId && !unitId) {
      // Qualification-level media: use getQualificationAttachments endpoint
      response = await axios.get(`${API_BASE_URL}/getQualificationAttachments`, {
        params: { qualificationId },
        headers: {
          'Authorization': `${token}`,
        },
      });
      
      // For qualification-level media, return all attachments directly
      // Don't filter them - they are already qualification-level by definition
      if (response.data?.result?.attachments) {
        return response.data.result.attachments;
      }
      return [];
      
    } else if (unitId && moduleId) {
      // Unit-level media: use getAttechmentByModules endpoint with all three IDs
      response = await axios.get(`${API_BASE_URL}/getAttechmentByModules`, {
        params: { qualificationId, moduleId, unitId },
        headers: {
          'Authorization': `${token}`,
        },
      });
    } else if (moduleId && !unitId) {
      // Module-level media: use getAttechmentByUnits endpoint
      response = await axios.get(`${API_BASE_URL}/getAttechmentByUnits`, {
        params: { qualificationId, moduleId },
        headers: {
          'Authorization': `${token}`,
        },
      });
    }
    
    
    // Handle different response structures for module/unit level
    let attachments = [];
    if (response.data?.resource?.attachments) {
      attachments = response.data.resource.attachments;
    } else if (response.data?.result?.attachments) {
      attachments = response.data.result.attachments;
    } else if (response.data?.attachments) {
      attachments = response.data.attachments;
    } else if (Array.isArray(response.data)) {
      attachments = response.data;
    }
    
    
    // Apply filtering only for module/unit level requests
    let filteredAttachments = [];
    
    if (unitId && moduleId) {
      // For unit requests: filter for attachments that have this unitId in their units array
      filteredAttachments = attachments.filter(attachment => {
        const hasCorrectQualification = attachment.post_to === qualificationId;
        const hasUnitInArray = attachment.units && 
                               Array.isArray(attachment.units) && 
                               attachment.units.includes(unitId);
        
        
        return hasCorrectQualification && hasUnitInArray;
      });
    } else if (moduleId && !unitId) {
      // For module requests: filter for attachments that have moduleId and EMPTY units array
      filteredAttachments = attachments.filter(attachment => {
        const hasCorrectQualification = attachment.post_to === qualificationId;
        const hasModuleInArray = attachment.modules && 
                                Array.isArray(attachment.modules) && 
                                attachment.modules.includes(moduleId);
        // Check for empty units array (not null or undefined)
        const hasEmptyUnits = !attachment.units || 
                             attachment.units.length === 0;
        
        
        return hasCorrectQualification && hasModuleInArray && hasEmptyUnits;
      });
    }
    
    
    return filteredAttachments;
    
  } catch (error) {
    if (error.response?.status === 404 || error.response?.status === 422) {
      return [];
    }
    
    console.error('Error fetching media attachments:', error);
    throw new Error(`Error fetching media attachments: ${error.message}`);
  }
};
export const updateStandaloneSubjects = async (courseId, subjects) => {
  const token = localStorage.getItem('token');
  const startedAt = Date.now();
  try {
    const response = await axios.patch(`${API_BASE_URL}/updateStandaloneSubjects`, { courseId, subjects }, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
      // Without a timeout a stalled request leaves the Save button on
      // "Saving…" forever; fail loudly instead.
      timeout: REQUEST_TIMEOUT_MS,
    });
    if (response.data) {
      return response.data;
    } else {
      throw new Error('Failed to update standalone subjects');
    }
  } catch (error) {
    console.error(`Error updating standalone subjects after ${Date.now() - startedAt}ms:`, error.response?.data || error.message);
    if (isTimeout(error)) {
      throw new Error('The server did not respond in time. Your subjects may not have saved — please check and try again.');
    }
    throw new Error(error.response?.data?.message || 'Error updating standalone subjects');
  }
};
